import { Button, XIcon } from '@tovala/component-library'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { events } from 'analytics/events'
import { useUser } from 'contexts/user'
import { TermToRate } from 'types/internal'
import { track } from 'utils/analytics'

import MealRatingDialog from './MealRatingDialog'
import MultiMealRatingDialog from './MultiMealRatingDialog'
import {
  getReviewSourceID,
  hasViewedRatingOnboarding,
  joinMealIDs,
  MealToRate,
} from './utils'

type TermMealToRate = TermToRate['meals'][number]

type BannerRatingDialog =
  | { type: 'multi'; mealsToRate: MealToRate[] }
  | { type: 'single'; meal: TermMealToRate }

function getMealsToRateFromTermMeals(
  termMeals: TermMealToRate[],
): MealToRate[] {
  return termMeals.map((meal) => ({ cookedAt: null, meal }))
}

const RateMealsPrompt = ({
  mostRecentTermMealsToRate,
  onClickClose,
}: {
  mostRecentTermMealsToRate: TermToRate | undefined
  onClickClose?: () => void
}) => {
  const { pathname } = useLocation()
  const { user } = useUser()
  const [openRatingDialog, setOpenRatingDialog] =
    useState<BannerRatingDialog | null>(null)

  const termMealsToRate = mostRecentTermMealsToRate?.meals ?? []
  const hasMealsToRate = termMealsToRate.length > 0

  if (!hasMealsToRate && !openRatingDialog) {
    return null
  }

  return (
    <>
      {hasMealsToRate && (
        <div className="relative w-full rounded-xl bg-black p-4 text-white">
          {onClickClose && (
            <div className="flex justify-end">
              <button
                aria-label="Close"
                className="h-6 w-6 cursor-pointer text-white"
                onClick={onClickClose}
              >
                <XIcon />
              </button>
            </div>
          )}
          <div className="flex flex-wrap justify-between gap-y-6">
            <div>
              <h2 className="mb-4 text-h/34_100 font-semibold">
                Help us improve our food
              </h2>
              <p className="text-k/16_125">
                Rate meals to get more of what you love (and less of what you
                don't) on our menu
              </p>
            </div>

            <Button
              buttonStyle="white"
              onClick={() => {
                const ratingDialog: BannerRatingDialog =
                  hasViewedRatingOnboarding(user.id) &&
                  termMealsToRate.length > 1
                    ? {
                        type: 'multi',
                        mealsToRate:
                          getMealsToRateFromTermMeals(termMealsToRate),
                      }
                    : { type: 'single', meal: termMealsToRate[0] }

                setOpenRatingDialog(ratingDialog)
                track(events.REVIEW_PROMPT_MODAL_APPEARS, {
                  meal_id:
                    ratingDialog.type === 'multi'
                      ? joinMealIDs(
                          ratingDialog.mealsToRate.map(({ meal }) => meal.id),
                        )
                      : ratingDialog.meal.id,
                  source_id: getReviewSourceID({ pathname }),
                })
              }}
              size="medium"
            >
              Rate a Meal
            </Button>
          </div>
        </div>
      )}

      {openRatingDialog?.type === 'single' && (
        <MealRatingDialog
          meal={openRatingDialog.meal}
          onClose={() => setOpenRatingDialog(null)}
        />
      )}
      {openRatingDialog?.type === 'multi' && (
        <MultiMealRatingDialog
          mealsToRate={openRatingDialog.mealsToRate}
          onClose={() => setOpenRatingDialog(null)}
        />
      )}
    </>
  )
}

export default RateMealsPrompt
